import { createContext, Dispatch, useContext, useEffect, useState } from 'react';
import { Typography } from 'antd';
import { Image } from 'react-bootstrap';
import FileIcon from '../../assets/icons/file.svg';
import { setSelectedKeys, setSelectModel } from '../../service/apiSlices/staticApi';
import ModelStatusPendingIcon from "../../assets/icons/pendingIconGenerate.svg";
import { GeneratedModelTypes } from '../types/ModelsTypes';
import DateTime from '../../components/DateTime/DateTime';
import React from 'react';

export interface ModelListParams {
  activeFilter: string;
  search: string;
  page: number;
  limit: number;
  format_type?: number;
  type?: string;
}

interface renderModelCardProps {
  model: GeneratedModelTypes;
  selectModel: boolean;
  selectedModuleIds: number[];
  onModelClick: (model: GeneratedModelTypes) => void;
  onModelChecked: (id: number, key: string) => void;
  showPendingStatus?: boolean;
}

export const getModelListParams = (params: ModelListParams) => {
  const { activeFilter, search, page, limit, format_type, type } = params;

  const baseParams = {
    activeFilter: activeFilter === "all" ? 0 :
      activeFilter === "created" ? 1 :
        activeFilter === "team" ? 2 : 3,
    search,
    page,
    limit,
    ...(format_type && { format_type })
  };

  if (type) {
    return { ...baseParams, listType: type };
  }

  return baseParams;
};

export const useModelOperations = (dispatch: Dispatch<any>) => {
  const handleModelSelection = (
    id: number,
    key: string,
    selectedModuleIds: number[],
    setSelectedModuleIds: (ids: number[]) => void,
    setSelectModelKeys?: (keys: ((prevKeys: string[]) => string[])) => void
  ) => {
    const updatedIds = selectedModuleIds.includes(id)
      ? selectedModuleIds.filter((item) => item !== id)
      : [...selectedModuleIds, id];

    setSelectedModuleIds(updatedIds);

    setSelectModelKeys && setSelectModelKeys((prevKeys: string[]) => {
      const updatedKeys = selectedModuleIds.includes(id)
        ? prevKeys.filter((item: any) => item !== key)
        : [...prevKeys, key];

      dispatch(setSelectedKeys(updatedKeys));
      return updatedKeys;
    });
  };

  const resetModelSelection = (setSelectedModuleIds: (ids: number[]) => void) => {
    setSelectedModuleIds([]);
    dispatch(setSelectedKeys([]));
    dispatch(setSelectModel(false));
  };

  return {
    handleModelSelection,
    resetModelSelection
  };
};

export const renderEmptyState = (activeFilter: string, search: string, length: number) => (
  <div className="d-flex align-items-center justify-content-center col-md-12 empty-models-section">
    <Typography.Text className="empty-models-text">
      {
        search && search.length > 0 ? (
          <span className="no-models-text">{`No results found for "${search}"`}</span>
        ) : (
          <span className="no-models-text">Your {activeFilter} models will be displayed here</span>
        )
      }
    </Typography.Text>
  </div>
);

export const renderModelCard = ({
  model,
  selectModel,
  selectedModuleIds,
  onModelClick,
  onModelChecked,
  showPendingStatus = false
}: renderModelCardProps) => (
  <div className="model-wrapper col-md-4 col-lg-3 col-xl-2" key={model.id}>
    <button
      className="p-2 model-button position-relative"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        if ((e.target as HTMLElement).tagName === 'INPUT') return;
        onModelClick(model);
      }}
    >
      {selectModel && !(model?.file_type === "viewport-draft") && (
        <input
          type="checkbox"
          className="position-absolute model-checkbox"
          style={{ top: "5px", right: "5px", opacity: selectModel ? "1" : "0" }}
          onChange={(e) => {
            e.stopPropagation();
            onModelChecked(model.id, model.secret_key);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              onModelChecked(model.id, model.secret_key);
            }
          }}
          checked={selectedModuleIds.includes(model.id)}
        />
      )}
      <div className="text-center">
        <div className="position-relative d-inline-block">
          <Image src={FileIcon} className="model-icon" />
          {showPendingStatus && !model?.is_model_completed && (
            <Image
              src={ModelStatusPendingIcon}
              className="position-absolute pending-icon"
            />
          )}
        </div>
        <div className="text-center">
          <Typography.Text
            ellipsis
            title={model.workspace_name ?? model.filename}
            className="model-card-name"
          >
            {model.workspace_name ?? model.filename}
          </Typography.Text>
        </div>
        <div className="text-center">
          <Typography.Text className="created-time-text">
            Created on{" "}
            <DateTime
              dateTime={model.created_on}
              showAgo
              showTooltip
              tooltipProps={{ placement: "bottom" }}
            />
          </Typography.Text>
        </div>
      </div>
    </button>
  </div>
);

const ModalContainerContext = createContext<HTMLElement | null>(null);

// Modal Container Provider Component
export const ModalContainerProvider = ({ children }: { children: React.ReactNode }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const dashboardCard = document.querySelector('.dashboard-main-card');
    if (dashboardCard) {
      const modalContainer = document.createElement('div');
      modalContainer.className = 'modal-container';
      modalContainer.style.position = 'absolute';
      modalContainer.style.top = '0';
      modalContainer.style.left = '0';
      modalContainer.style.width = '100%';
      modalContainer.style.height = '100%';
      modalContainer.style.zIndex = '1000';

      dashboardCard.appendChild(modalContainer);
      setContainer(modalContainer);

      return () => {
        dashboardCard.removeChild(modalContainer);
      };
    }
  }, []);

  return (
    <ModalContainerContext.Provider value={container}>
      {children}
    </ModalContainerContext.Provider>
  );
};

// Custom hook to use modal container
export const useModalContainer = () => {
  return React.useContext(ModalContainerContext);
};

// Create context for managing active modal
const ActiveModalContext = createContext<{
  activeModal: string | null;
  setActiveModal: (modalId: string | null) => void;
}>({
  activeModal: null,
  setActiveModal: () => { },
});

// Provider component for active modal management
export const ActiveModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeModal, setActiveModal] = useState<string | null>(null);

  return (
    <ActiveModalContext.Provider value={{ activeModal, setActiveModal }}>
      {children}
    </ActiveModalContext.Provider>
  );
};

export const useActiveModal = () => useContext(ActiveModalContext);